import { InfoCard } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useState, useEffect } from 'react';
import { ComponentsGrid } from './componentsGrid';
import { squadNameFormat } from './userFetch';
import { useUser, GroupMemberships } from '../../../context/userContext';
import TextLink from '../../text-link';
import { Link } from '@backstage/core-components';
import { formatSquadName } from '../../../Utils/squadName';

type UserContext = {
  kind: string;
  metadata: UserMetaData;
  relations: UserRelation[];
  spec: SpecData;
};

type SpecData = {
  type: string;
};

type UserMetaData = {
  name: string;
  namespace: string;
};

type UserRelation = {
  target: RelationTarget[];
  targetRef: string;
  type: string;
};

type RelationTarget = {
  kind: string;
  name: string;
  namespace: string;
};

type ComponentGridProp = {
  counter: number;
  type?: string;
  kind?: string;
  url: string;
};

const useStyles = makeStyles({
  description: {
    padding: '3px 0 40px',
  },
});

function buildUrl(
  squads: GroupMemberships[],
  pageName: string,
  kind: string,
  type: string,
) {
  let url = `/${pageName}?filters%5Bkind%5D=${kind}&filters%5Btype%5D=${type}&filters%5Buser%5D=all`;
  if (squads) {
    squads.map(squadObj => {
      const squadName = squadNameFormat(squadObj.squad);
      url = `${url}&filters%5Bowners%5D=${squadName}`;
    });
  }
  return url;
}

interface UserWidgetProps {
  data: UserContext[];
  groupMemberships: GroupMemberships[];
}

const UserWidget = ({ data, groupMemberships }: UserWidgetProps) => {
  const [userName, setUserName] = useState('Jane Doe');
  const classes = useStyles();
  const identity = useApi(identityApiRef);

  useEffect(() => {
    (async () => {
      const profileInfo = await identity.getProfileInfo();

      if (profileInfo.displayName?.includes(',')) {
        const userNameSlice = profileInfo.displayName?.split(',');
        return (
          userNameSlice &&
          setUserName(`${userNameSlice[1]} ${userNameSlice[0]}`)
        );
      }
      return (
        profileInfo &&
        profileInfo.displayName &&
        setUserName(profileInfo.displayName)
      );
    })();
  }, [identity]);

  const documentationAmount = data
    ? data.filter(x => x.spec !== undefined && x.spec.type === 'documentation')
        .length
    : 0;
  const serviceAmount = data
    ? data.filter(x => x.spec !== undefined && x.spec.type === 'service').length
    : 0;
  const apiAmount = data
    ? data.filter(x => x.spec !== undefined && x.spec.type === 'openapi').length
    : 0;
  const websiteAmount = data
    ? data.filter(x => x.spec !== undefined && x.spec.type === 'website').length
    : 0;

  const docsUrl = buildUrl(
    groupMemberships,
    'catalog',
    'component',
    'documentation',
  );
  const apiURL = buildUrl(groupMemberships, 'catalog', 'api', '');
  const servicesURL = buildUrl(
    groupMemberships,
    'catalog',
    'component',
    'service',
  );
  const websitesURL = buildUrl(
    groupMemberships,
    'catalog',
    'component',
    'website',
  );

  const propVar: ComponentGridProp[] = [
    { counter: serviceAmount, url: servicesURL, type: 'Services' },
    { counter: apiAmount, url: apiURL, type: 'APIs' },
    { counter: websiteAmount, url: websitesURL, type: 'Websites' },
    { counter: documentationAmount, url: docsUrl, type: 'Documentation Sites' },
  ];

  const cleanSquadName = (squadName: string) => {
    const cleanedName = formatSquadName(squadName);

    return cleanedName.replaceAll('-', ' ');
  };

  const renderDescription = () => {
    switch (groupMemberships.length) {
      case 0:
        return (
          <Alert severity="warning">
            Your team is not registered with the{' '}
            <TextLink
              href="https://ablcode.visualstudio.com/DefaultCollection/Polaris/_git/platform-registration?path=/onboarding/squads.yaml"
              color="primary"
              external
            >
              Squad Registry
            </TextLink>
            . Open up a PR against the Squad Registry manifest to add your
            squad.
          </Alert>
        );
      case 1:
        return (
          <p>
            You're a member of the{' '}
            <Link
              to={`/catalog/default/group/${formatSquadName(
                groupMemberships[0].squad,
              )}`}
              color="primary"
              style={{ fontWeight: 'bold' }}
            >
              {cleanSquadName(groupMemberships[0].squad)}
            </Link>
            . Navigate to your registered software components below.
          </p>
        );
      default:
        return (
          <p>
            You're a member of the{' '}
            {groupMemberships.map((membership, index) => {
              const squadLink = (
                <Link
                  to={`/catalog/default/group/${formatSquadName(
                    membership.squad,
                  )}`}
                  color="primary"
                  style={{ fontWeight: 'bold' }}
                >
                  {cleanSquadName(membership.squad)}
                </Link>
              );

              if (index < groupMemberships.length - 1 && index > 0) {
                return <span>, the {squadLink}</span>;
              } else if (index === groupMemberships.length - 1) {
                return <span> and the {squadLink}</span>;
              }

              return squadLink;
            })}
            . Navigate to your registered software components below.
          </p>
        );
    }
  };

  return (
    <div>
      <h4>Welcome, {userName}</h4>
      <div className={classes.description}>{renderDescription()}</div>
      {groupMemberships.length > 0 && (
        <ComponentsGrid
          userContextObj={propVar}
          groupMemberships={groupMemberships}
        />
      )}
    </div>
  );
};

export default UserWidget;
